'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import dynamic from 'next/dynamic';
import HistoryTable from './HistoryTable';
import HistoryFilters from './HistoryFilters';
import HistoryEmptyState from './HistoryEmptyState';
import DeleteConfirmModal from './DeleteConfirmModal';

const QRCodeCanvas = dynamic(() => import('qrcode.react').then(m => m.QRCodeCanvas), { ssr: false });

export interface HistoryRecord {
  id: string;
  type: string;
  content: string;
  options: {
    fgColor: string;
    bgColor: string;
    transparentBg: boolean;
    style: string;
    logoUrl: string | null;
    logoSize: number;
    errorLevel: string;
    size: number;
  };
  createdAt: string;
  downloadCount: number;
}

// Mock data — Backend integration point: GET /api/qr/history
const mockHistory: HistoryRecord[] = [
  { id: 'qr-001', type: 'url', content: 'https://knowourindia.com', options: { fgColor: '#0F172A', bgColor: '#FFFFFF', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'M', size: 512 }, createdAt: '2026-08-03T10:22:00Z', downloadCount: 7 },
  { id: 'qr-002', type: 'upi', content: 'upi://pay?pa=novamax@okaxis&pn=NovaMax%20Studios&am=499&tn=Service%20payment', options: { fgColor: '#1E40AF', bgColor: '#EFF6FF', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'H', size: 1024 }, createdAt: '2026-08-03T09:15:00Z', downloadCount: 3 },
  { id: 'qr-003', type: 'whatsapp', content: 'https://wa.me/919876543210?text=Hello%21%20I%27d%20like%20to%20connect', options: { fgColor: '#166534', bgColor: '#F0FDF4', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'M', size: 512 }, createdAt: '2026-08-02T16:45:00Z', downloadCount: 12 },
  { id: 'qr-004', type: 'wifi', content: 'WIFI:T:WPA;S:NovaMax_Office_5G;P:Secure@2026;;', options: { fgColor: '#92400E', bgColor: '#FFFBEB', transparentBg: false, style: 'square', logoUrl: null, logoSize: 20, errorLevel: 'L', size: 256 }, createdAt: '2026-08-02T14:30:00Z', downloadCount: 2 },
  { id: 'qr-005', type: 'email', content: 'mailto:hello@knowourindia.com?subject=Inquiry&body=Hi%2C%20I%27d%20like%20to%20learn%20more', options: { fgColor: '#4C1D95', bgColor: '#F5F3FF', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'Q', size: 512 }, createdAt: '2026-08-01T11:20:00Z', downloadCount: 5 },
  { id: 'qr-006', type: 'url', content: 'https://novamaxstudios.com/portfolio', options: { fgColor: '#0F172A', bgColor: '#FFFFFF', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'M', size: 512 }, createdAt: '2026-08-01T09:05:00Z', downloadCount: 18 },
  { id: 'qr-007', type: 'maps', content: 'https://maps.google.com/?q=India+Gate%2C+New+Delhi', options: { fgColor: '#9F1239', bgColor: '#FFF1F2', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'M', size: 512 }, createdAt: '2026-07-31T15:00:00Z', downloadCount: 4 },
  { id: 'qr-008', type: 'phone', content: 'tel:+919876543210', options: { fgColor: '#0E7490', bgColor: '#ECFEFF', transparentBg: false, style: 'square', logoUrl: null, logoSize: 20, errorLevel: 'L', size: 256 }, createdAt: '2026-07-31T12:40:00Z', downloadCount: 1 },
  { id: 'qr-009', type: 'text', content: 'Certificate of Completion — NovaMax Studios — Batch 2026', options: { fgColor: '#374151', bgColor: '#F9FAFB', transparentBg: false, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'H', size: 1024 }, createdAt: '2026-07-30T10:00:00Z', downloadCount: 23 },
  { id: 'qr-010', type: 'url', content: 'https://knowourindia.com/states/rajasthan', options: { fgColor: '#0F172A', bgColor: '#FFFFFF', transparentBg: true, style: 'rounded', logoUrl: null, logoSize: 20, errorLevel: 'M', size: 512 }, createdAt: '2026-07-29T08:30:00Z', downloadCount: 9 },
];

export type FilterType = 'all' | 'url' | 'text' | 'whatsapp' | 'phone' | 'email' | 'maps' | 'wifi' | 'upi';

export default function QRHistoryClient() {
  const [records, setRecords] = useState<HistoryRecord[]>(mockHistory);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string | 'bulk' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 8;

  useEffect(() => {
    // Backend integration point: GET /api/qr/history
    // Merge localStorage with mock for demo
    const stored = JSON.parse(localStorage.getItem('nova-qr-history') || '[]') as HistoryRecord[];
    const merged = [...stored, ...mockHistory].reduce((acc: HistoryRecord[], r) => {
      if (!acc.find(x => x.id === r.id)) acc.push(r);
      return acc;
    }, []);
    setRecords(merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    setTimeout(() => setIsLoading(false), 600);
  }, []);

  const filtered = useMemo(() => {
    return records.filter(r => {
      const matchSearch = !search || r.content.toLowerCase().includes(search.toLowerCase()) || r.type.toLowerCase().includes(search.toLowerCase());
      const matchType = filterType === 'all' || r.type === filterType;
      return matchSearch && matchType;
    });
  }, [records, search, filterType]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleDelete = useCallback((id: string) => {
    setDeleteTarget(id);
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setDeleteTarget('bulk');
  }, [selectedIds]);

  const confirmDelete = useCallback(() => {
    if (deleteTarget === 'bulk') {
      setRecords(prev => prev.filter(r => !selectedIds.has(r.id)));
      toast.success(`${selectedIds.size} QR codes deleted.`);
      setSelectedIds(new Set());
    } else if (deleteTarget) {
      setRecords(prev => prev.filter(r => r.id !== deleteTarget));
      toast.success('QR code deleted.');
    }
    setDeleteTarget(null);
    // Backend integration point: DELETE /api/qr/history/:id or bulk
  }, [deleteTarget, selectedIds]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) setSelectedIds(new Set(paginated.map(r => r.id)));
    else setSelectedIds(new Set());
  }, [paginated]);

  const handleSelectRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleRedownload = useCallback((record: HistoryRecord) => {
    // Backend integration point: POST /api/qr/history/:id/download
    setRecords(prev => prev.map(r => r.id === record.id ? { ...r, downloadCount: r.downloadCount + 1 } : r));
    toast.success('QR code re-downloaded!');
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 xl:px-10 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-border bg-card">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-extrabold text-foreground" style={{ fontSize: 'clamp(22px, 6vw, 26px)', letterSpacing: '-0.02em' }}>QR History</h1>
            <p className="text-muted-foreground mt-1" style={{ fontSize: '14px' }}>
              {records.length} QR codes generated — search, filter, re-download, or delete.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-50 text-rose-600 border border-rose-200 font-semibold transition-all hover:bg-rose-100 w-full sm:w-auto justify-center"
                style={{ fontSize: '13px', minHeight: '44px' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M2 4h10M5 4V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V4M11.5 4l-.5 8a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9L2.5 4" />
                </svg>
                Delete {selectedIds.size} selected
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 xl:px-10 py-5 sm:py-6">
        <div className="max-w-screen-2xl mx-auto space-y-5">
          <HistoryFilters
            search={search}
            onSearchChange={v => { setSearch(v); setCurrentPage(1); }}
            filterType={filterType}
            onFilterChange={v => { setFilterType(v); setCurrentPage(1); }}
            totalCount={filtered.length}
          />

          {isLoading ? (
            <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <div key={`skel-row-${i + 1}`} className="flex items-center gap-4 px-6 py-4 border-b border-border last:border-0">
                  <div className="animate-pulse bg-muted rounded-xl w-12 h-12 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="animate-pulse bg-muted rounded h-3.5 w-48" />
                    <div className="animate-pulse bg-muted rounded h-3 w-32" />
                  </div>
                  <div className="animate-pulse bg-muted rounded-full w-16 h-6" />
                  <div className="animate-pulse bg-muted rounded h-3 w-20" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <HistoryEmptyState hasSearch={!!search || filterType !== 'all'} />
          ) : (
            <HistoryTable
              records={paginated}
              selectedIds={selectedIds}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              onDelete={handleDelete}
              onRedownload={handleRedownload}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={filtered.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {deleteTarget && (
        <DeleteConfirmModal
          isOpen={true}
          isBulk={deleteTarget === 'bulk'}
          bulkCount={deleteTarget === 'bulk' ? selectedIds.size : 0}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}